import { useEffect } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { App } from "antd5";

import { BID_SELF_SERVE_CHANGED } from "components/account_management/qualification-criteria/BidSelfServe";
import { BOOK_A_CALL_LINK } from "components/account_management/utils";
import PaidContentBanner from "components/ui/banner/PaidContentBanner";
import { BreadcrumbContainer, SimpleBreadcrumb } from "components/ui/breadcrumb/SimpleBreadcrumb";
import Button from "components/ui/button/Button";
import { BidNoBidResponse } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { PqlActionType } from "lib/types/graphQLEnums";
import { BidReport } from "../../../lib/hooks/api/bid/bid_reports/useBidReport";
import { Flex, Text } from "../../../styles/utility-components";
import { BidNoBidHeaderActionButtons } from "./BidNoBidHeaderActionButtons";

type Props = {
  bidReportId: string;
  bidInfo: BidReport | undefined;
  bidId: string;
  data: BidNoBidResponse | undefined;
  urlShareSignature?: string;
  slideoutOpen: boolean;
  setSlideoutOpen: (open: boolean) => void;
  onRegenerateBidReport: () => void;
};

export function BidNoBidReportHeader({
  bidReportId,
  bidInfo,
  bidId,
  data,
  urlShareSignature,
  slideoutOpen,
  setSlideoutOpen,
  onRegenerateBidReport,
}: Props) {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const subscription = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const { logEvent } = useTracking();

  const { message } = App.useApp();

  const { mutate: actionPql } = usePqlActioned(undefined, undefined, false);

  // Show banner if the user is freemium and is here on credits
  const hasBidAccess = subscription.hasDataTypes("BIDS");

  // If a user has navigated here from bid-self-serve, we must check if they've
  // changed their bid questions, if they have then we show an info toast
  useEffect(() => {
    if (sessionStorage.getItem(BID_SELF_SERVE_CHANGED)) {
      message.open({
        key: "bidSelfServeMessage",
        type: "info",
        icon: <UIcon icon="infoCircle" size={16} color={colors.sysInfoDefault} />,
        content: (
          <MessageContent>
            <Text>Report outdated, regenerate to see new criteria</Text>
            <Button
              icon="xClose"
              onClick={() => message.destroy("bidSelfServeMessage")}
              iconProps={{
                size: 16,
                color: colors.sysTextDefault,
              }}
              buttonStyle="inverse"
            />
          </MessageContent>
        ),
        duration: 0, // Stays open until manually closed
      });
      sessionStorage.removeItem(BID_SELF_SERVE_CHANGED);
    }
    // Cleanup on unmount/navigation
    return () => {
      message.destroy("bidSelfServeMessage");
      sessionStorage.removeItem(BID_SELF_SERVE_CHANGED);
    };
  }, [colors.sysInfoDefault, colors.sysTextDefault, message]);

  return (
    <>
      {!!currentUser && !hasBidAccess && (
        <PaidContentBanner
          paidPlanType="bidPart"
          backgroundColor={colors.sysDestructiveSubtleAlt}
          iconSize={20}
          description={
            <>
              Available as part of Bid Studio.{" "}
              <UpgradeLink
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  logEvent(EventNames.paywallActioned, {
                    Action: "Book a demo in-app",
                    "Context source": "Bid studio",
                  });
                  actionPql({
                    actionType: PqlActionType.BookADemoInApp,
                    dataType: "BIDS",
                    pageUrl: window.location.href,
                  });
                }}
                href={BOOK_A_CALL_LINK}
              >
                Book a call
              </UpgradeLink>
            </>
          }
        />
      )}
      {/* Only show breadcrumbs if we have bidInfo returned - should show for both logged in users and non logged in users */}
      {bidInfo && (
        <BreadcrumbContainer>
          <SimpleBreadcrumb
            items={[
              {
                label: "Bids",
                path: currentUser ? routes.bids : undefined,
              },
              {
                label: bidInfo.bid?.name || "Linked bid",
                path: currentUser ? `${routes.bids}/${bidId}` : undefined,
              },
              { label: "Requirement summary" },
            ]}
            onNavigate={(path: string) => {
              if (currentUser) {
                void navigate({ to: path });
              } else {
                void navigate({ to: "/auth/login" });
              }
            }}
          />
          <BidNoBidHeaderActionButtons
            bidReportId={bidReportId}
            bidInfo={bidInfo}
            data={data}
            urlShareSignature={urlShareSignature}
            onRegenerateBidReport={onRegenerateBidReport}
            slideoutOpen={slideoutOpen}
            setSlideoutOpen={setSlideoutOpen}
          />
        </BreadcrumbContainer>
      )}
    </>
  );
}

const UpgradeLink = styled.a(({ theme }) => ({
  fontWeight: 500,
  color: theme.colors.sysTextDefault,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    color: theme.colors.sysTextDefault,
  },
}));

const MessageContent = styled(Flex)({
  marginLeft: "8px",
  alignItems: "center",
  gap: "8px",
});
